import React, { useEffect, useState, useCallback } from 'react';
import { serviceAPI } from '../api';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Power, PowerOff, X, ArrowLeft, GripVertical, Building, Compass, Hammer, Sofa, Home, Wrench, Ruler, Palette } from 'lucide-react';
import { Link } from 'react-router-dom';

const ICON_OPTIONS = [
  { name: 'Building', Icon: Building },
  { name: 'Compass', Icon: Compass },
  { name: 'Hammer', Icon: Hammer },
  { name: 'Sofa', Icon: Sofa },
  { name: 'Home', Icon: Home },
  { name: 'Wrench', Icon: Wrench },
  { name: 'Ruler', Icon: Ruler },
  { name: 'Palette', Icon: Palette }
];

const COLOR_OPTIONS = ['#F59E0B', '#2563EB', '#22C55E', '#EF4444', '#F97316', '#8B5CF6', '#06B6D4', '#EC4899', '#14B8A6', '#84CC16'];

const IconRenderer = ({ name, className }) => {
  const item = ICON_OPTIONS.find(o => o.name === name);
  const Icon = item ? item.Icon : Building;
  return <Icon className={className} />;
};

export const ServiceManagementPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    name: '', description: '', color: '#F59E0B', icon: 'Building', display_order: 0, is_active: true,
    deliverables: [], default_sla_days: 7, is_package: false, deliverable_config: [], checklist_template: [], config_version: 1
  });

  const fetchServices = useCallback(async () => {
    try {
      const { data } = await serviceAPI.getAll();
      setServices(data);
    } catch (error) {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchServices(); }, [fetchServices]);

  const openCreate = () => {
    setEditingService(null);
    setFormData({ name: '', description: '', color: '#F59E0B', icon: 'Building', display_order: services.length + 1, is_active: true, deliverables: [], default_sla_days: 7, is_package: false, deliverable_config: [], checklist_template: [], config_version: 1 });
    setShowModal(true);
  };

  const openEdit = (svc) => {
    setEditingService(svc);
    setFormData({
      name: svc.name,
      description: svc.description || '',
      color: svc.color || '#F59E0B',
      icon: svc.icon || 'Building',
      display_order: svc.display_order || 0,
      is_active: svc.is_active,
      deliverables: svc.deliverables || [],
      default_sla_days: svc.default_sla_days || 7,
      is_package: !!svc.is_package,
      deliverable_config: svc.deliverable_config || [],
      checklist_template: svc.checklist_template || [],
      config_version: svc.config_version || 1
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingService) {
        await serviceAPI.update(editingService.id, formData);
        toast.success('Service updated');
      } else {
        await serviceAPI.create(formData);
        toast.success('Service created');
      }
      setShowModal(false);
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (svc) => {
    if (!window.confirm(`Delete "${svc.name}"? Leads using this service will not be affected.`)) return;
    try {
      await serviceAPI.delete(svc.id);
      toast.success('Service deleted');
      fetchServices();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to delete';
      if (error.response?.status === 409) {
        const reason = window.prompt(`${detail}\n\nArchive it instead? Enter a reason:`);
        if (!reason) return;
        try {
          await serviceAPI.archive(svc.id, { reason });
          toast.success('Service archived — project history stays intact');
          fetchServices();
          return;
        } catch (e2) {
          toast.error(e2.response?.data?.detail || 'Archive failed');
          return;
        }
      }
      toast.error(detail);
    }
  };

  const handleToggle = async (svc) => {
    try {
      await serviceAPI.toggleActive(svc.id);
      toast.success(`Service ${svc.is_active ? 'disabled' : 'enabled'}`);
      fetchServices();
    } catch (error) {
      toast.error('Failed to toggle');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>;
  }

  return (
    <div className="space-y-6" data-testid="services-container">
      <div className="flex items-center gap-4">
        <Link to="/settings" className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-4xl font-outfit font-semibold">Service Management</h1>
          <p className="text-muted-foreground mt-2">Manage the services offered by your business</p>
        </div>
        <button data-testid="add-service-btn" onClick={openCreate} className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg">
          <Plus className="h-4 w-4" /> Add Service
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {services.map(svc => (
          <div key={svc.id} data-testid={`service-card-${svc.name}`}
            className={`clay-card p-5 hover-lift ${!svc.is_active ? 'opacity-60' : ''}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${svc.color}20` }}>
                <IconRenderer name={svc.icon} className="h-6 w-6" style={{ color: svc.color }} />
              </div>
              <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${
                svc.is_active ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-slate-100 text-slate-500 dark:bg-slate-800'
              }`}>
                <div className={`w-1.5 h-1.5 rounded-full ${svc.is_active ? 'bg-green-500' : 'bg-slate-400'}`}></div>
                {svc.is_active ? 'Active' : 'Disabled'}
              </span>
            </div>
            <h3 className="font-outfit font-semibold text-lg mb-1">{svc.name}</h3>
            <p className="text-sm text-muted-foreground mb-4 min-h-[40px]">{svc.description || '—'}</p>
            <div className="flex items-center gap-1 pt-3 border-t border-slate-200 dark:border-slate-700">
              <button data-testid={`edit-service-${svc.name}`} onClick={() => openEdit(svc)} title="Edit"
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors">
                <Edit className="h-4 w-4" />
              </button>
              <button onClick={() => handleToggle(svc)} title={svc.is_active ? 'Disable' : 'Enable'}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors">
                {svc.is_active ? <PowerOff className="h-4 w-4 text-orange-600" /> : <Power className="h-4 w-4 text-green-600" />}
              </button>
              <button data-testid={`delete-service-${svc.name}`} onClick={() => handleDelete(svc)} title="Delete"
                className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors ml-auto">
                <Trash2 className="h-4 w-4 text-red-600" />
              </button>
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <GripVertical className="h-3 w-3" />
                Order: {svc.display_order || 0}
              </span>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white dark:bg-[#131A2B] rounded-xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-outfit font-semibold">{editingService ? 'Edit Service' : 'Add Service'}</h2>
              <button onClick={() => setShowModal(false)}><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <input required data-testid="service-name-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary/50 bg-white dark:bg-slate-900" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea data-testid="service-desc-input" rows={3} value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary/50 bg-white dark:bg-slate-900" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Color</label>
                <div className="flex flex-wrap gap-2">
                  {COLOR_OPTIONS.map(c => (
                    <button key={c} type="button" onClick={() => setFormData({ ...formData, color: c })}
                      className={`w-8 h-8 rounded-full border-2 ${formData.color === c ? 'border-slate-900 dark:border-white ring-2 ring-offset-2 ring-primary' : 'border-transparent'}`}
                      style={{ backgroundColor: c }} />
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Icon</label>
                <div className="grid grid-cols-8 gap-2">
                  {ICON_OPTIONS.map(({ name, Icon }) => (
                    <button key={name} type="button" onClick={() => setFormData({ ...formData, icon: name })}
                      className={`p-2 border rounded-lg ${formData.icon === name ? 'border-primary bg-primary/10' : 'border-slate-200 dark:border-slate-700'}`}>
                      <Icon className="h-4 w-4 mx-auto" />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <label className="block text-sm font-medium">Deliverables included in this service / package</label>
                  <span data-testid="service-config-version" className="rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
                    Config v{formData.config_version || 1}
                  </span>
                </div>
                <textarea data-testid="service-deliverables-input" rows={3}
                  value={(formData.deliverables || []).join('\n')}
                  onChange={(e) => setFormData({ ...formData, deliverables: e.target.value.split('\n').map(x => x.trim()).filter(Boolean) })}
                  placeholder={'One deliverable per line, e.g.\nFloor Plan\nElevation'}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900" />
                <p className="mt-1 text-xs text-muted-foreground">Architects only ever see deliverables of the services actually sold to the client. Live projects keep the configuration they were created with.</p>
                {(formData.deliverables || []).length > 0 && (
                  <div className="mt-3 min-w-0 max-w-full overflow-x-auto rounded-lg border dark:border-slate-700" data-testid="deliverable-rules-table">
                    <table className="w-full min-w-[520px] text-xs">
                      <thead className="bg-slate-50 text-left dark:bg-slate-800">
                        <tr>
                          <th className="p-2">Deliverable</th><th className="p-2">Required</th><th className="p-2">Active</th>
                          <th className="p-2">SLA</th><th className="p-2">Review</th><th className="p-2">Client Appr.</th><th className="p-2">IFC</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(formData.deliverables || []).map((n, i) => {
                          const cfg = (formData.deliverable_config || []).find(c => c.name === n) || {};
                          const set = (patch) => {
                            const rest = (formData.deliverable_config || []).filter(c => c.name !== n);
                            setFormData({ ...formData, deliverable_config: [...rest, { name: n, order: i,
                              required: cfg.required !== false, active: cfg.active !== false,
                              requires_internal_review: cfg.requires_internal_review !== false,
                              requires_client_approval: cfg.requires_client_approval !== false,
                              requires_ifc: !!cfg.requires_ifc, default_sla_days: cfg.default_sla_days || null, ...patch }] });
                          };
                          const box = (key, val, testid) => (
                            <td className="p-2"><input type="checkbox" data-testid={`${testid}-${i}`} checked={val}
                              onChange={(e) => set({ [key]: e.target.checked })} /></td>
                          );
                          return (
                            <tr key={n} className="border-t dark:border-slate-700" data-testid={`deliverable-rule-row-${i}`}>
                              <td className="p-2 font-medium">{n}</td>
                              {box('required', cfg.required !== false, 'rule-required')}
                              {box('active', cfg.active !== false, 'rule-active')}
                              <td className="p-2"><input type="number" min={1} placeholder="svc" data-testid={`rule-sla-${i}`}
                                value={cfg.default_sla_days || ''} onChange={(e) => set({ default_sla_days: parseInt(e.target.value) || null })}
                                className="w-16 rounded border px-1 py-0.5 dark:border-slate-700 dark:bg-slate-900" /></td>
                              {box('requires_internal_review', cfg.requires_internal_review !== false, 'rule-review')}
                              {box('requires_client_approval', cfg.requires_client_approval !== false, 'rule-approval')}
                              {box('requires_ifc', !!cfg.requires_ifc, 'rule-ifc')}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Document checklist template (one per line, add * for required)</label>
                <textarea data-testid="service-checklist-input" rows={3}
                  value={(formData.checklist_template || []).map(c => `${c.required ? '*' : ''}${c.label}`).join('\n')}
                  onChange={(e) => setFormData({ ...formData, checklist_template: e.target.value.split('\n')
                    .map(x => x.trim()).filter(Boolean)
                    .map((x, i) => ({ label: x.replace(/^\*/, '').trim(), required: x.startsWith('*'), active: true, order: i })) })}
                  placeholder={'*Registry\nSite Photos'}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900" />
                <p className="mt-1 text-xs text-muted-foreground">Copied into each new project at creation — later edits never change existing project checklists.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Default SLA (days)</label>
                  <input type="number" min={1} data-testid="service-sla-input" value={formData.default_sla_days}
                    onChange={(e) => setFormData({ ...formData, default_sla_days: parseInt(e.target.value) || 1 })}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900" />
                </div>
                <label className="flex items-end gap-2 pb-2 text-sm">
                  <input type="checkbox" data-testid="service-package-checkbox" checked={formData.is_package}
                    onChange={(e) => setFormData({ ...formData, is_package: e.target.checked })} />
                  This is a package
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Display Order</label>
                <input type="number" value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="svc_active" checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} />
                <label htmlFor="svc_active" className="text-sm">Active</label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg">Cancel</button>
                <button type="submit" data-testid="service-submit" className="px-4 py-2 bg-primary text-white rounded-lg">
                  {editingService ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
